import { memo, useCallback } from "react";
import { InfoTooltip } from "../../common/components";
import { Flexbox } from "../../core/components";
import { VendorDataRestictions, VendorDataEntryModel, VendorDataErrorMessages } from "../models";
import { type VendorDataChangedAction } from "../vendorDataReducer";
import { VendorDataInput } from "./VendorDataInput";

interface VendorDataValueInputProps {
    entry: VendorDataEntryModel;
    disabled: boolean;
    onVendorDataChanged: (event: VendorDataChangedAction) => void;
}

export const VendorDataValueInput = memo<VendorDataValueInputProps>(function VendorDataValueInput({
    entry,
    onVendorDataChanged,
    disabled,
}) {
    const {
        data: { value },
    } = entry;

    const updateValue = useCallback(
        (newValue: string, isValid: boolean) => {
            onVendorDataChanged({
                type: "update",
                entry: entry.setValue(newValue, isValid),
            });
        },
        [entry, onVendorDataChanged],
    );

    const validateValue = useCallback(
        (newValue: string) => !!newValue && VendorDataRestictions.allowedValueRegex.test(newValue),
        [],
    );

    const valueError = useCallback((newValue: string) => {
        if (!newValue) {
            return VendorDataErrorMessages.ShouldNotBeEmpty;
        }

        const matches = VendorDataRestictions.deniedSymbolRegex.exec(newValue);

        if (matches && matches.length > 0) {
            return `${VendorDataErrorMessages.ValueContainsWrongSymbol} ${matches[0]}`;
        }

        return null;
    }, []);

    return (
        <VendorDataInput
            name="dataValue"
            value={value ?? ""}
            label={
                <Flexbox>
                    Attribute Value
                    <InfoTooltip content="Value of Vendor Data attribute" />
                </Flexbox>
            }
            disabled={disabled}
            maxLength={VendorDataRestictions.maxValueLength}
            onChanged={updateValue}
            validator={validateValue}
            error={valueError}
            data-testid={`vendor-data-value-input-${entry.index}`}
        />
    );
});
